import React, { useEffect, useState, forwardRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useSnackbar,
} from '@ui-framework/index';
import RuleConditions from '../RuleConditions';

const WorkflowDisplay = forwardRef((props, ref) => {
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const getData = async url => {
    const result = await get(url);
    if (result.ok) {
      return result?.data?.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={result?.errors} />,
    });
    return null;
  };

  const initialize = async workflowId => {
    const workflowStepsData = await getData(
      `${getApiUrl()}/sxcl/v1/workflows/${workflowId}/steps`,
    );

    const workflowStepRules = [];

    if (workflowStepsData) {
      workflowStepsData.forEach(workflowStep => {
        workflowStepRules.push(
          getData(
            `${getApiUrl()}/sxcl/v1/rule-groups/${
              workflowStep?.ruleGroupId
            }/rules?limit=100`,
          ),
        );
      });

      const workflowStepRulesResult = await Promise.all(workflowStepRules);
      const updatedWorkflowStepsData = workflowStepsData?.map(
        (workflowStep, key) => ({
          ...workflowStep,
          rules: workflowStepRulesResult[key],
        }),
      );
      setWorkflowSteps(updatedWorkflowStepsData);
    }
  };

  const { getValues } = useFormContext();
  const workflowId = getValues('id');

  useEffect(() => {
    initialize(workflowId);
  }, [workflowId]);

  return (
    <Paper ref={ref} elevation={0} sx={{ ml: 2, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Steps
      </Typography>
      <Divider />
      <List>
        {workflowSteps.map((step, index) => (
          <Box key={step?.id} mb={2}>
            <ListItem alignItems="flex-start" disableGutters>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1" fontWeight="bold">
                      {index + 1}. {step?.ruleGroupName} ({step?.rules.length})
                    </Typography>
                  </Stack>
                }
                secondary={
                  <div>
                    <Chip
                      label={`Stop On Event: ${step?.stopOnEvent}`}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <br />
                    <Chip
                      label={`Stop On Failure Event: ${step?.stopOnFailureEvent}`}
                      size="small"
                    />
                  </div>
                }
              />
            </ListItem>
            {step?.rules && step?.rules?.length > 0 && (
              <Box ml={4} mt={1}>
                <Typography variant="subtitle2">Rules:</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Conditions</TableCell>
                      <TableCell>Event</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {step?.rules.map(({ rule }) => (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <Link to={`/rules/edit/${rule.id}`}>{rule.name}</Link>
                        </TableCell>
                        <TableCell>{rule.priority}</TableCell>
                        <TableCell>
                          <RuleConditions conditions={rule.conditions} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            <Chip
                              label={rule.event.type}
                              size="small"
                              color={
                                rule.event.type &&
                                rule.event.type.toLowerCase().includes('error')
                                  ? 'error'
                                  : 'primary'
                              }
                              variant="outlined"
                              sx={{ mr: 1, mb: 1 }}
                            />{' '}
                            <br />
                            Field: {rule.event.params.field || '-'} <br />
                            Message: {rule.event.params.message || '-'} <br />
                            Error: {rule.event.params.errorCode || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        ))}
      </List>
    </Paper>
  );
});

export default WorkflowDisplay;
